import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Item, PaneType, Section, UNSECTIONED_ITEMS_ID} from '../catalog-manager.models';
import {getImageUrl} from '../catalog-manager.utils';

@Component({
    selector: 'app-section-tree',
    templateUrl: './section-tree.component.html',
    styleUrls: ['./section-tree.component.scss']
})
export class SectionTreeComponent {
    @Input() sections: Section[] = [];
    @Input() items: Item[] = [];
    @Input() selectedSectionId: number | null = null;
    @Input() currentRootSectionId: number | null = null;
    @Input() expandedSections: Set<number> = new Set();
    @Input() parentId: number | null = null;
    @Input() level: number = 0;
    @Input() pane: PaneType = 'left';
    @Input() markedItemIds: Set<number> = new Set();

    @Output() sectionSelected = new EventEmitter<number | null>();
    @Output() sectionNavigated = new EventEmitter<number | null>();
    @Output() sectionToggled = new EventEmitter<number>();
    @Output() sectionRenamed = new EventEmitter<{ id: number; name: string }>();
    @Output() sectionDeleted = new EventEmitter<number>();
    @Output() sectionParentChanged = new EventEmitter<{ sectionId: number; newParentId: number | null }>();
    @Output() itemCreationRequested = new EventEmitter<number | null>();
    @Output() sectionCreationRequested = new EventEmitter<number | null>();
    @Output() markAllItemsRequested = new EventEmitter<number>();
    @Output() itemsCopiedToSection = new EventEmitter<{ itemIds: number[]; toSectionId: number }>();
    @Output() itemsMovedToSection = new EventEmitter<{
        itemIds: number[];
        fromSectionId: number;
        toSectionId: number
    }>();

    editingSection: number | null = null;
    editingName: string = '';
    draggedSection: number | null = null;
    failedImages = new Set<number>();

    getImageUrl = getImageUrl;

    constructor() {
    }

    hasValidImage(id: number): boolean {
        return !this.failedImages.has(id);
    }

    onImageError(event: Event) {
        const img = event.target as HTMLImageElement;
        const urlMatch = img.src.match(/images\/(\d+)_main/);
        if (urlMatch) {
            const id = parseInt(urlMatch[1], 10);
            this.failedImages.add(id);
        }
        img.style.display = 'none';
    }

    getRootSections(): Section[] {
        return this.sections.filter(s => s.top_sec_id === null);
    }

    getCurrentSection(): Section | null {
        if (this.currentRootSectionId === null) {
            return null;
        }
        return this.sections.find(s => s.id === this.currentRootSectionId) || null;
    }

    getChildSections(parentId: number): Section[] {
        return this.sections.filter(s => s.top_sec_id === parentId);
    }

    getSectionsForParent(): Section[] {
        // Use currentRootSectionId if at root level, otherwise use parentId
        const effectiveParentId = this.level === 0 ? this.currentRootSectionId : this.parentId;
        return this.sections.filter(s => s.top_sec_id === effectiveParentId);
    }

    getChildCount(sectionId: number): number {
        return this.sections.filter(s => s.top_sec_id === sectionId).length;
    }

    getItemCount(sectionId: number): number {
        // Special case for unsectioned items
        if (sectionId === UNSECTIONED_ITEMS_ID) {
            return this.items.filter(item => item.section_ids.length === 0).length;
        }
        return this.items.filter(item => item.section_ids.includes(sectionId)).length;
    }

    isExpanded(sectionId: number): boolean {
        return this.expandedSections.has(sectionId);
    }

    onSectionClick(sectionId: number) {
        this.sectionSelected.emit(sectionId);
    }

    onSectionNavigate(sectionId: number) {
        // Single click navigates into the section
        // this.events.publish('navigate_' + this.pane);
        this.sectionNavigated.emit(sectionId);
    }

    onSectionDoubleClick(sectionId: number) {
        // Double click navigates into the section
        this.sectionNavigated.emit(sectionId);
    }

    onToggleExpand(event: Event, sectionId: number) {
        event.stopPropagation();
        this.sectionToggled.emit(sectionId);
    }

    startEdit(event: Event, section: Section) {
        event.stopPropagation();
        this.editingSection = section.id;
        this.editingName = section.name;
    }

    saveEdit(newName:string, sectionId: number) {
        if (newName.trim()) {
            this.sectionRenamed.emit({id: sectionId, name: newName.trim()});
        }
        this.editingSection = null;
    }

    cancelEdit() {
        this.editingSection = null;
    }

    deleteSection(event: Event, sectionId: number) {
        event.stopPropagation();
        if (confirm('Are you sure you want to delete this section?')) {
            this.sectionDeleted.emit(sectionId);
        }
    }

    onDragStart(event: DragEvent, sectionId: number) {
        this.draggedSection = sectionId;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', sectionId.toString());
        }
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
    }

    onDrop(event: DragEvent, targetSectionId: number | null) {
        event.preventDefault();
        event.stopPropagation();

        if (this.draggedSection !== null && this.draggedSection !== targetSectionId) {
            this.sectionParentChanged.emit({
                sectionId: this.draggedSection,
                newParentId: targetSectionId
            });
        }
        this.draggedSection = null;
    }

    onAddItem(event: Event, sectionId: number | null) {
        event.stopPropagation();
        this.itemCreationRequested.emit(sectionId);
    }

    onAddSection(event: Event, sectionId: number | null) {
        event.stopPropagation();
        this.sectionCreationRequested.emit(sectionId);
    }

    onMarkAllItems(event: Event, sectionId: number) {
        event.stopPropagation();
        this.markAllItemsRequested.emit(sectionId);
    }

    onCopyMarkedItemsHere(event: Event, toSectionId: number) {
        event.stopPropagation();
        if (this.markedItemIds.size === 0) {
            alert('No items marked to copy');
            return;
        }
        const itemIds = Array.from(this.markedItemIds);
        this.itemsCopiedToSection.emit({itemIds, toSectionId});
    }

    onMoveMarkedItemsHere(event: Event, toSectionId: number) {
        event.stopPropagation();
        if (this.markedItemIds.size === 0) {
            alert('No items marked to move');
            return;
        }
        // We need to know the fromSectionId, which is the currentRootSectionId
        if (this.currentRootSectionId === null) {
            alert('Cannot move from root level');
            return;
        }
        const itemIds = Array.from(this.markedItemIds);
        this.itemsMovedToSection.emit({itemIds, fromSectionId: this.currentRootSectionId, toSectionId});
    }

    trackById(index: number, section: Section): number {
        return section.id;
    }

    noop() {
        // Do nothing - used for cases where we don't want navigation
    }

    noopWithEvent(event: Event) {
        // Do nothing - used for disabled actions that pass an event
        event.stopPropagation();
    }

    noopWithDragEvent(event: DragEvent) {
        // Prevent drag functionality for special sections
        event.preventDefault();
        event.stopPropagation();
    }

    // Expose the constant for template use
    readonly UNSECTIONED_ITEMS_ID = UNSECTIONED_ITEMS_ID;

    // Create a reusable section object for the unsectioned items
    readonly unsectionedItemsSection: Section = {
        id: UNSECTIONED_ITEMS_ID,
        name: 'פריטים ללא קטגוריה',
        top_sec_id: null
    };
}
